import { useEffect, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

interface PortalProps {
  children: React.ReactNode
}

/**
 * Portal Component v3 - Production Ready
 * 
 * - Creates shared container if needed
 * - Removes container when last portal unmounts (with delay)
 * - Handles rapid mount/unmount cycles
 * - Checks portal wrapper count (not just any children)
 * - No global counters (avoids race conditions)
 * 
 * Phase 0.2: Fixed cleanup logic (v9)
 */
export function Portal({ children }: PortalProps) {
  const [mounted, setMounted] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const portalId = useRef(`portal-${Math.random().toString(36).slice(2, 11)}`)
  
  const createContainer = useCallback(() => {
    let container = document.getElementById('radar-portal-root') as HTMLDivElement | null
    
    if (!container) {
      container = document.createElement('div')
      container.id = 'radar-portal-root'
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9999;
      `
      document.body.appendChild(container)
    }
    
    return container
  }, [])
  
  const cleanupContainer = useCallback(() => {
    const container = document.getElementById('radar-portal-root')
    if (!container) return
    
    // Count portal wrappers (not just any children)
    const portalWrappers = container.querySelectorAll('[data-portal-id]')
    
    if (portalWrappers.length === 0 && document.body.contains(container)) {
      document.body.removeChild(container)
    }
  }, [])
  
  useEffect(() => {
    const container = createContainer()
    containerRef.current = container
    
    // Create wrapper div immediately
    const wrapper = document.createElement('div')
    wrapper.setAttribute('data-portal-id', portalId.current)
    container.appendChild(wrapper)
    wrapperRef.current = wrapper
    
    setMounted(true)
    
    return () => {
      setMounted(false)
      
      // Remove our wrapper first
      if (wrapperRef.current && wrapperRef.current.parentNode) {
        wrapperRef.current.parentNode.removeChild(wrapperRef.current)
      }
      wrapperRef.current = null
      
      // Then cleanup container after delay
      setTimeout(() => {
        cleanupContainer()
      }, 100)
    }
  }, [createContainer, cleanupContainer])
  
  if (!mounted || !wrapperRef.current) return null
  
  // Render into our wrapper, not directly into container
  return createPortal(children, wrapperRef.current)
}
